import { substrNFromEnd, truncateWithEllipses } from '../../util/format';
import { PaymentCategory, PaymentProfile } from '../../__generated__/pge-types';
import { isIphoneChrome } from '../../util/mobile/iphoneBrowser-validate';

export const isOtherType = (type?: string | null) =>
  type &&
  [
    PaymentCategory.PaypalAccount,
    PaymentCategory.PaypalCredit,
    PaymentCategory.AmazonPay,
    'GP',
    'AP',
    'VENMO',
  ].some(e => e.toString() === type);

export const isBankAccountType = (type?: string | null) =>
  type && (type === 'CHQ' || type === 'SAV');

export const isDebitCardType = (type?: string | null) =>
  type && type.indexOf('DEBIT') >= 0;

export const isCreditCardType = (type?: string | null) =>
  type &&
  !isBankAccountType(type) &&
  !isDebitCardType(type) &&
  !isOtherType(type);

export const getPmCategoryFromType = (type?: string | null) =>
  isBankAccountType(type)
    ? PaymentCategory.Dd
    : isDebitCardType(type)
    ? PaymentCategory.Dc
    : isCreditCardType(type)
    ? PaymentCategory.Cc
    : Object.values(PaymentCategory).find(val => val === type);

export const getAccountDisplayString = (
  paymentProfile?: PaymentProfile | null,
  emailTruncateLen: number = 25,
) => {
  if (!paymentProfile) {
    return '';
  }

  const { accountNumber, type, cardHolderName } = paymentProfile;

  const pmCategory = getPmCategoryFromType(type);

  if (
    pmCategory === PaymentCategory.AmazonPay ||
    pmCategory === PaymentCategory.PaypalAccount ||
    pmCategory === PaymentCategory.PaypalCredit ||
    pmCategory === PaymentCategory.Ap ||
    pmCategory === PaymentCategory.Venmo
  ) {
    return truncateWithEllipses(
      cardHolderName ? cardHolderName : accountNumber,
      emailTruncateLen,
    );
  }

  const formattedAccountNumber =
    accountNumber &&
    type &&
    pmCategory === PaymentCategory.Dd &&
    accountNumber.includes('-')
      ? String(accountNumber.split('-').splice(1, 1))
      : accountNumber;

  const maskedNumber = substrNFromEnd(10, formattedAccountNumber);

  return maskedNumber && maskedNumber.length < 10
    ? `${new Array(10 - maskedNumber.length + 1).join('*')}${maskedNumber}`
    : maskedNumber;
};

export const addEditExcludeList = [
  'PAYPAL_ACCOUNT',
  'PAYPAL_CREDIT',
  'AMAZON_PAY',
  'GP',
  'AP',
  'VENMO',
];

export const getAllowedPaymentCategories = (
  savedProfileList: PaymentProfile[] = [],
  defaultAllowed: PaymentCategory[] = [
    PaymentCategory.Dd,
    PaymentCategory.Cc,
    PaymentCategory.Dc,
  ],
  maxLimit?: Record<string, number>,
  isApplePayEnabled: boolean = false,
): PaymentCategory[] => {
  const paymentMethodLimit: Record<string, number> = maxLimit || {
    [PaymentCategory.PaypalAccount]: 1,
    ...(isApplePayEnabled ? { [PaymentCategory.Ap]: 1 } : {}),
    ...(!isIphoneChrome() ? { [PaymentCategory.Venmo]: 1 } : {}),
  };

  return [
    ...defaultAllowed,
    ...Object.keys(paymentMethodLimit)
      .filter(type => isApplePayEnabled || type !== PaymentCategory.Ap)
      .flatMap(type =>
        savedProfileList.filter(p => p.type === type)?.length <
        paymentMethodLimit[type]
          ? [type as PaymentCategory]
          : [],
      )
      .filter(Boolean),
  ];
};
